/**
 * 원격지원 클라이언트 서비스
 *
 *  - 상태 스토어: 내가 원격지원 담당자인가 + 정책 스위치(GET /api/remote-support/me, 소켓으로 갱신)
 *  - 요청 버스: 프로필 모달의 '원격지원 요청' → RemoteSupportRoot(담당자 뷰어)
 *  - RsPeer: 정확히 2자(직원 ↔ 담당자) WebRTC 연결. **직원(host)이 offer를 낸다** — 스트림을 가진 쪽이 initiator다.
 *    DataChannel 2개: 'mv'(이동, 순서·재전송 없음 — 최신 값만 의미) / 'ev'(클릭·키·휠·글자, 순서·재전송 보장).
 *    클릭·키는 좌표를 함께 실어 'mv'와 순서가 어긋나도 엉뚱한 곳을 누르지 않는다.
 *  - InjectPump: 직원 PC에서 받은 입력을 Rust(rs_inject)로 한 번에 하나씩 넘긴다.
 *  - startNativeCapture: 직원 PC의 **모든 모니터**를 Rust(remote_capture.rs)가 떠서 JPEG 조각으로 보내면, 캔버스에 덧그려
 *    MediaStreamTrackGenerator 트랙으로 만든다. getDisplayMedia는 쓰지 않는다 — 선택 창에 '모든 화면'이 없다(docs §14).
 *
 * ⚠ callService(통화)를 재사용하지 않는다 — 카메라 sender의 replaceTrack이라 통화가 없으면 동작하지 않고,
 *   싱글톤 peers Map을 공유하면 hangup()이 서로를 부순다(docs/remote-support-2026-10.md §4.7).
 */
import { useSyncExternalStore } from 'react';
import { apiFetch } from './apiClient';
import { getIceServers } from '../utils/iceServers';
import { RemoteInputEvent, ScreenRect, countMeaningfulInputs } from '../utils/remoteInput';
import { parseCaptureMessage } from '../utils/remoteCaptureProtocol';
import { isTauri } from '../utils/tauri';

// ─────────────────────────── 상태 스토어 ───────────────────────────

export interface RemoteSupportMe {
  /** 서버에서 한 번이라도 받았는가 — 받기 전에는 버튼을 숨긴다 */
  loaded: boolean;
  isHelpdesk: boolean;
  enabled: boolean;
  controlEnabled: boolean;
  /** 원격지원 중 파일 주고받기. 구버전 서버는 필드가 없다 → 켜짐으로 본다(서버가 rs:file:start에서 최종 판정) */
  fileTransferEnabled: boolean;
}

const EMPTY_ME: RemoteSupportMe = { loaded: false, isHelpdesk: false, enabled: false, controlEnabled: false, fileTransferEnabled: false };
let me: RemoteSupportMe = EMPTY_ME;
const meListeners = new Set<() => void>();

function setMe(next: RemoteSupportMe) {
  // 값이 같으면 참조를 유지한다(useSyncExternalStore 무한 렌더 방지).
  // ⚠ 필드를 추가하면 여기에도 넣을 것 — 빠뜨리면 그 필드만 바뀐 정책 푸시가 조용히 무시된다
  if (
    next.loaded === me.loaded &&
    next.isHelpdesk === me.isHelpdesk &&
    next.enabled === me.enabled &&
    next.controlEnabled === me.controlEnabled &&
    next.fileTransferEnabled === me.fileTransferEnabled
  ) {
    return;
  }
  me = next;
  meListeners.forEach((l) => l());
}

export function getRemoteSupportMe(): RemoteSupportMe {
  return me;
}

/** 부팅·재연결·권한 변경 시. 실패하면 이전 값을 유지한다(버튼이 깜빡이지 않게) */
export async function refreshRemoteSupportMe(): Promise<void> {
  try {
    const r = await apiFetch<{ isHelpdesk: boolean; enabled: boolean; controlEnabled: boolean; fileTransferEnabled?: boolean }>(
      '/remote-support/me'
    );
    setMe({
      loaded: true,
      isHelpdesk: !!r.isHelpdesk,
      enabled: !!r.enabled,
      controlEnabled: !!r.controlEnabled,
      fileTransferEnabled: !!r.enabled && r.fileTransferEnabled !== false,
    });
  } catch (err) {
    console.warn('[RemoteSupport] /me failed:', err);
  }
}

/** 소켓 config:remote-support — 정책만 바뀐다(담당자 여부는 그대로) */
export function applyRemoteSupportPolicy(
  p: { enabled?: boolean; controlEnabled?: boolean; fileTransferEnabled?: boolean } | null | undefined
) {
  if (!p) return;
  setMe({
    ...me,
    enabled: !!p.enabled,
    controlEnabled: !!p.controlEnabled,
    fileTransferEnabled: !!p.enabled && p.fileTransferEnabled !== false,
  });
}

/** 로그아웃 — 다음 계정에 이전 계정의 담당자 여부가 남지 않게 */
export function resetRemoteSupportMe() {
  setMe(EMPTY_ME);
}

export function useRemoteSupportMe(): RemoteSupportMe {
  return useSyncExternalStore(
    (l) => {
      meListeners.add(l);
      return () => {
        meListeners.delete(l);
      };
    },
    getRemoteSupportMe,
    getRemoteSupportMe
  );
}

// ─────────────────────────── 요청 버스 ───────────────────────────

export interface RemoteSupportTarget {
  userId: string;
  name: string;
  avatar?: string | null;
  depart?: string | null;
  /** 프로필 API가 찾아 준 계열사 — 사번이 계열사끼리 겹칠 수 있어 서버가 대상을 특정하는 데 쓴다 */
  affiliateId?: string | null;
}

const requestListeners = new Set<(t: RemoteSupportTarget) => void>();

export function requestRemoteSupport(target: RemoteSupportTarget) {
  requestListeners.forEach((l) => l(target));
}

export function onRemoteSupportRequest(listener: (t: RemoteSupportTarget) => void): () => void {
  requestListeners.add(listener);
  return () => {
    requestListeners.delete(listener);
  };
}

// 직원 → 담당자 요청 버스: 담당자 프로필의 '원격지원 요청' → RemoteSupportHost(직원 PC, Tauri에서만 마운트)
const askListeners = new Set<(t: RemoteSupportTarget) => void>();

/** 이 PC 화면을 봐 달라고 담당자에게 요청한다. 듣는 쪽이 없으면(웹) false — 호출부가 안내를 띄운다 */
export function askRemoteSupport(target: RemoteSupportTarget): boolean {
  if (askListeners.size === 0) return false;
  askListeners.forEach((l) => l(target));
  return true;
}

export function onRemoteSupportAsk(listener: (t: RemoteSupportTarget) => void): () => void {
  askListeners.add(listener);
  return () => {
    askListeners.delete(listener);
  };
}

// ─────────────────────────── 기록 REST ───────────────────────────

export interface RemoteSessionRecord {
  sessionCode: string;
  operatorName: string;
  hostName: string;
  requestedAt: string;
  acceptedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  controlSeconds: number;
  endReason: string | null;
  endedBy: string | null;
  /** 'host' = 직원이 먼저 요청. 구버전 서버는 없다 */
  initiatedBy?: 'host' | 'operator';
  /** 주고받은 파일 수(담당자 → 직원 / 직원 → 담당자). 구버전 서버는 없다 */
  filesToHost?: number;
  filesToOperator?: number;
  note: string | null;
}

export const remoteSupportApi = {
  /** 내 PC에서 있었던 원격지원(직원 본인) */
  mySessions: () => apiFetch<RemoteSessionRecord[]>('/remote-support/my-sessions'),
  /** 내가 지원한 세션(담당자) */
  operatorSessions: () => apiFetch<RemoteSessionRecord[]>('/remote-support/operator-sessions'),
  saveNote: (code: string, note: string) =>
    apiFetch<{ success: boolean }>(`/remote-support/sessions/${encodeURIComponent(code)}/note`, {
      method: 'PUT',
      body: JSON.stringify({ note }),
    }),
};

// ─────────────────────────── Tauri 네이티브 ───────────────────────────

export interface MonitorBrief {
  device: string;
  primary: boolean;
  left: number;
  top: number;
  width: number;
  height: number;
  scalePercent: number;
}

export interface PcSummary {
  computerName: string;
  userName: string;
  os: string;
  osBuild: string;
  cpu: string;
  ramTotalMb: number;
  ramFreeMb: number;
  diskTotalGb: number;
  diskFreeGb: number;
  ip: string | null;
  uptimeHours: number;
  appVersion: string;
  monitors: MonitorBrief[];
  /** 지금 보내는 캡처 프레임(직원 PC의 모든 모니터 = 가상 데스크톱). 구버전 PC앱(모니터 하나 송출)은 없다 */
  capture?: ScreenRect | null;
}

/** rs_session_begin(보기 동의 → 세션) 결과 */
export interface SessionLayout {
  capture: ScreenRect;
  monitors: MonitorBrief[];
}

/** Rust 커맨드 호출(src-tauri/src/remote_support.rs). PC 앱이 아니면 거부 */
export async function rsInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  if (!isTauri()) throw new Error('not_tauri');
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(cmd, args);
}

/** Rust가 보내는 이벤트 구독 — 해제 함수를 동기로 돌려준다(언마운트가 먼저 와도 새지 않게) */
export function listenNative<T>(event: string, handler: (payload: T) => void): () => void {
  let disposed = false;
  let unlisten: (() => void) | null = null;
  if (isTauri()) {
    import('@tauri-apps/api/event')
      .then(({ listen }) => listen<T>(event, (e) => handler(e.payload)))
      .then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
      .catch((err) => console.warn(`[RemoteSupport] listen ${event} failed:`, err));
  }
  return () => {
    disposed = true;
    unlisten?.();
  };
}

// ─────────────────────────── 화면 송출(직원 PC) ───────────────────────────

/** TS 4.9 lib.dom에 WebCodecs·Breakout Box 타입이 없어 쓰는 만큼만 선언한다(WebView2 = Chromium은 둘 다 있다) */
interface VideoFrameLike {
  close(): void;
}
type VideoFrameCtor = new (src: OffscreenCanvas, init: { timestamp: number }) => VideoFrameLike;
type TrackGeneratorCtor = new (init: { kind: 'video' }) => MediaStreamTrack & { writable: WritableStream<VideoFrameLike> };

export interface NativeCapture {
  stream: MediaStream;
  stop: () => void;
}

/** 디코딩이 이보다 많이 밀리면 쌓인 조각을 버리고 전체 한 장을 다시 청한다 */
const CAPTURE_QUEUE_MAX = 6;
/**
 * 화면이 멈춰 있어도 이 간격으로 마지막 그림을 다시 넣는다. 프레임이 끊기면 인코더가 키프레임 요청(PLI)에 답하지 못해
 * 패킷 손실 뒤 담당자 화면이 굳는다
 */
const CAPTURE_REPEAT_MS = 1000;

/**
 * 직원 PC: 모든 모니터 화면을 트랙 하나로. Rust는 rs_session_begin(네이티브 보기 동의)으로 만든 세션이 살아 있을 때만
 * 보낸다 — 세션이 없으면 rs_capture_start가 거부한다. 메시지 형식은 utils/remoteCaptureProtocol.ts.
 */
export async function startNativeCapture(sessionId: string): Promise<NativeCapture> {
  const w = window as unknown as { MediaStreamTrackGenerator?: TrackGeneratorCtor; VideoFrame?: VideoFrameCtor };
  const Gen = w.MediaStreamTrackGenerator;
  const Frame = w.VideoFrame;
  if (!Gen || !Frame || typeof OffscreenCanvas === 'undefined') throw new Error('capture_unsupported');
  const { Channel } = await import('@tauri-apps/api/core');

  const track = new Gen({ kind: 'video' });
  const writer = track.writable.getWriter();
  let canvas: OffscreenCanvas | null = null;
  let ctx: OffscreenCanvasRenderingContext2D | null = null;
  let needFull = true;
  let busy = false;
  let stopped = false;
  let lastWriteAt = 0;
  const queue: ArrayBuffer[] = [];

  const writeFrame = async () => {
    if (!canvas || stopped) return;
    const frame = new Frame(canvas, { timestamp: Math.round(performance.now() * 1000) });
    lastWriteAt = performance.now();
    try {
      await writer.write(frame); // 넘겨받은 쪽이 프레임을 닫는다
    } catch {
      frame.close();
    }
  };

  const requestFull = () => {
    needFull = true;
    rsInvoke('rs_capture_full').catch(() => {});
  };

  const apply = async (buf: ArrayBuffer) => {
    const msg = parseCaptureMessage(buf);
    if (!msg) {
      requestFull();
      return;
    }
    if (msg.full) {
      if (!canvas || canvas.width !== msg.width || canvas.height !== msg.height) {
        canvas = new OffscreenCanvas(msg.width, msg.height);
        ctx = canvas.getContext('2d');
      }
      needFull = false;
    } else if (needFull || !canvas || canvas.width !== msg.width || canvas.height !== msg.height) {
      return; // 시작·화면 구성 변경·따라잡기 — 전체 한 장을 기다린다
    }
    const c = ctx;
    if (!c || msg.patches.length === 0) return;
    const bitmaps = await Promise.all(
      msg.patches.map(p => createImageBitmap(new Blob([p.jpeg], { type: 'image/jpeg' })))
    );
    bitmaps.forEach((b, i) => {
      if (!stopped) c.drawImage(b, msg.patches[i].x, msg.patches[i].y);
      b.close();
    });
    await writeFrame();
  };

  const pump = async () => {
    if (busy) return;
    busy = true;
    while (queue.length > 0 && !stopped) {
      const buf = queue.shift() as ArrayBuffer;
      try {
        await apply(buf);
      } catch (err) {
        console.warn('[RemoteSupport] capture frame failed:', err);
        requestFull();
      }
    }
    busy = false;
  };

  const channel = new Channel<ArrayBuffer>();
  channel.onmessage = (data) => {
    if (stopped) return;
    const buf = data instanceof ArrayBuffer ? data : new Uint8Array(data as unknown as number[]).buffer;
    queue.push(buf);
    if (queue.length > CAPTURE_QUEUE_MAX) {
      // 디코딩이 못 따라간다 — 쌓인 조각은 버리고 전체 한 장부터
      queue.length = 0;
      requestFull();
    }
    void pump();
  };
  const repeat = window.setInterval(() => {
    if (!busy && !needFull && canvas && performance.now() - lastWriteAt >= CAPTURE_REPEAT_MS) void writeFrame();
  }, 250);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(repeat);
    queue.length = 0;
    channel.onmessage = () => {};
    writer.close().catch(() => {});
    track.stop();
  };

  try {
    await rsInvoke('rs_capture_start', { sessionId, onFrame: channel });
  } catch (err) {
    stop();
    throw err;
  }
  return { stream: new MediaStream([track]), stop };
}

// ─────────────────────────── WebRTC ───────────────────────────

export interface RsSignal {
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
}

interface RsPeerOptions {
  onSignal: (data: RsSignal) => void;
  /** 담당자: 직원 화면 */
  onTrack?: (stream: MediaStream) => void;
  /** 직원: 담당자가 보낸 입력 */
  onInput?: (events: RemoteInputEvent[]) => void;
  /** 담당자: DataChannel 두 개가 다 열렸는가(아니면 소켓 폴백) */
  onChannelsChange?: (open: boolean) => void;
  onConnectionState?: (state: RTCPeerConnectionState) => void;
  /** 양쪽: 파일 주고받기 채널 'fx'(remoteFileTransfer.ts가 소유). 구버전 직원 앱은 만들지 않는다 */
  onFileChannel?: (ch: RTCDataChannel) => void;
}

/** 이동 이벤트는 이 이상 밀려 있으면 버린다(최신 값만 의미가 있다) */
const MOVE_BACKPRESSURE_BYTES = 256 * 1024;
const MAX_EVENTS_PER_MESSAGE = 200;

export class RsPeer {
  readonly pc: RTCPeerConnection;
  private pending: RTCIceCandidateInit[] = [];
  private mv: RTCDataChannel | null = null;
  private ev: RTCDataChannel | null = null;
  private fx: RTCDataChannel | null = null;
  private closed = false;

  constructor(private role: 'host' | 'operator', private opts: RsPeerOptions) {
    this.pc = new RTCPeerConnection({ iceServers: getIceServers() });
    this.pc.onicecandidate = (e) => {
      if (e.candidate) this.opts.onSignal({ candidate: e.candidate.toJSON() });
    };
    this.pc.onconnectionstatechange = () => this.opts.onConnectionState?.(this.pc.connectionState);
    if (role === 'operator') {
      this.pc.ontrack = (e) => this.opts.onTrack?.(e.streams[0] ?? new MediaStream([e.track]));
      this.pc.ondatachannel = (e) => this.attach(e.channel);
    }
  }

  /** 직원: 화면 트랙을 싣고 offer를 낸다. 모든 모니터 프레임은 크므로 비트레이트를 화소 수에 맞춰 넘긴다 */
  async startHost(stream: MediaStream, enc: { maxBitrate?: number; maxFramerate?: number } = {}): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('no_video_track');
    // 글자가 많은 화면 — 인코더에 선명도를 우선하라고 알린다
    try {
      track.contentHint = 'detail';
    } catch {
      /* 미지원 */
    }
    const sender = this.pc.addTrack(track, stream);
    this.attach(this.pc.createDataChannel('mv', { ordered: false, maxRetransmits: 0 }));
    this.attach(this.pc.createDataChannel('ev', { ordered: true }));
    // 파일 주고받기 — 재협상 경로가 없으므로 offer 전에 만든다. 담당자 화면은 이 채널이 있어야 [파일]을 켠다
    this.attach(this.pc.createDataChannel('fx', { ordered: true }));
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    if (this.pc.localDescription) this.opts.onSignal({ sdp: this.pc.localDescription.toJSON() });
    // 기본값은 대역폭이 줄면 해상도를 떨어뜨려 글자가 뭉개진다 — 프레임을 줄이고 해상도를 지킨다
    try {
      const params = sender.getParameters() as RTCRtpSendParameters & { degradationPreference?: string };
      params.degradationPreference = 'maintain-resolution';
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      params.encodings[0].maxBitrate = enc.maxBitrate ?? 2_500_000;
      params.encodings[0].maxFramerate = enc.maxFramerate ?? 15;
      await sender.setParameters(params);
    } catch (err) {
      console.warn('[RemoteSupport] setParameters failed (기본값으로 송출):', err);
    }
  }

  async handleSignal(data: RsSignal): Promise<void> {
    if (this.closed || !data) return;
    try {
      if (data.sdp) {
        await this.pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === 'offer') {
          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);
          if (this.pc.localDescription) this.opts.onSignal({ sdp: this.pc.localDescription.toJSON() });
        }
        for (const c of this.pending.splice(0)) {
          await this.pc.addIceCandidate(c).catch(() => {});
        }
      } else if (data.candidate) {
        if (this.pc.remoteDescription) await this.pc.addIceCandidate(data.candidate).catch(() => {});
        else this.pending.push(data.candidate);
      }
    } catch (err) {
      console.error('[RemoteSupport] signal handling failed:', err);
    }
  }

  private attach(ch: RTCDataChannel) {
    // 'fx'는 입력 채널이 아니다 — channelsOpen()(입력 폴백 판정)에 섞지 않고 소유자에게 넘기기만 한다
    if (ch.label === 'fx') {
      this.fx = ch;
      ch.binaryType = 'arraybuffer';
      this.opts.onFileChannel?.(ch);
      return;
    }
    if (ch.label === 'mv') this.mv = ch;
    else if (ch.label === 'ev') this.ev = ch;
    else return;
    const notify = () => this.opts.onChannelsChange?.(this.channelsOpen());
    ch.onopen = notify;
    ch.onclose = notify;
    if (this.role === 'host') {
      ch.onmessage = (e) => {
        if (typeof e.data !== 'string' || e.data.length > 64 * 1024) return;
        try {
          const msg = JSON.parse(e.data);
          if (Array.isArray(msg?.e)) this.opts.onInput?.(msg.e.slice(0, MAX_EVENTS_PER_MESSAGE));
        } catch {
          /* 깨진 메시지는 버린다 */
        }
      };
    }
  }

  channelsOpen(): boolean {
    return this.mv?.readyState === 'open' && this.ev?.readyState === 'open';
  }

  /** 담당자 → 직원. 채널이 안 열렸으면 false(호출부가 소켓 폴백) */
  send(kind: 'mv' | 'ev', events: RemoteInputEvent[]): boolean {
    const ch = kind === 'mv' ? this.mv : this.ev;
    if (!ch || ch.readyState !== 'open') return false;
    if (kind === 'mv' && ch.bufferedAmount > MOVE_BACKPRESSURE_BYTES) return true;
    ch.send(JSON.stringify({ e: events }));
    return true;
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    try {
      this.mv?.close();
      this.ev?.close();
      this.fx?.close();
    } catch {
      /* 이미 닫힘 */
    }
    this.pc.close();
  }
}

// ─────────────────────────── 입력 주입 펌프 (직원 PC) ───────────────────────────

const GATE_CLOSED_ERRORS = ['no_gate', 'bad_token', 'expired', 'idle'];
const MAX_QUEUE = 2000;

/**
 * 받은 입력을 Rust에 순서대로 넘긴다. 호출이 진행 중이면 모아 두었다가 다음 호출에 싣는다.
 * 게이트가 닫혔다는 응답(만료·회수)을 받으면 큐를 비우고 onGateClosed를 한 번 부른다.
 */
export class InjectPump {
  private queue: RemoteInputEvent[] = [];
  private busy = false;
  private pendingCount = 0;

  constructor(
    private getToken: () => string | null,
    private onGateClosed: (reason: string) => void
  ) {}

  push(events: RemoteInputEvent[]) {
    if (!this.getToken()) return;
    this.queue.push(...events);
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
    void this.flush();
  }

  /** 서버 집계용 — 마지막 보고 이후 주입한 건수를 꺼낸다 */
  takeCount(): number {
    const n = this.pendingCount;
    this.pendingCount = 0;
    return n;
  }

  clear() {
    this.queue = [];
  }

  private async flush() {
    if (this.busy) return;
    this.busy = true;
    try {
      while (this.queue.length > 0) {
        const token = this.getToken();
        if (!token) {
          this.queue = [];
          break;
        }
        const batch = this.queue.splice(0, MAX_EVENTS_PER_MESSAGE);
        try {
          await rsInvoke<number>('rs_inject', { token, events: batch });
          this.pendingCount += countMeaningfulInputs(batch);
        } catch (err) {
          const msg = String(err);
          if (GATE_CLOSED_ERRORS.some((e) => msg.includes(e))) {
            this.queue = [];
            this.onGateClosed(msg);
            break;
          }
          console.warn('[RemoteSupport] inject failed:', msg);
        }
      }
    } finally {
      this.busy = false;
    }
  }
}
